import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledViewBar from 'components/organisms/Bars/ViewBar/ViewBar.styles';
import Typography from 'components/atoms/Text/Typography/Typography';
import Close from '@mui/icons-material/Close';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import {useLocation} from 'react-router-dom';
import {useLinkNavigate} from 'helpers/hooks/links';
import LinearProgress from 'components/atoms/Progress/LinearProgress/LinearProgress';
import Box from 'components/atoms/Layout/Box/Box';
import Tooltip from 'components/atoms/Tooltips/Tooltip/Tooltip';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';

const ViewBar = React.forwardRef((props, ref) => {
  const {
    title,
    actions,
    progress,
    progressText,
    progressTooltip,
    isLoading,
    ...innerProps
  } = useComponentProps({
    color: 'primary',
    ...props
  }, 'ViewBar', {
    styled: ['color']
  });

  const tableProvider = useTable();
  const view = tableProvider?.view;

  const location = useLocation();
  const navigate = useLinkNavigate();

  const closeAction = useMemo(() => ({
    label: 'Close view',
    icon: Close,
    onClick: (e) => {
      navigate({event: e, to: location.pathname, resetView: true, resetSearchParams: false});
    },
    ButtonProps: {
      size: 'small',
      radius: 'square',
      variant: 'text'
    }
  }), [navigate, location]);

  const renderTitle = () => {
    const viewTitle = utils.isFunction(title ?? view.title) ? (title ?? view.title)(view) : (title ?? view.title);

    if (viewTitle) {
      if (utils.isReactElement(viewTitle)) {
        return viewTitle;
      } else {
        return <Typography variant="body2"><Markdown>{viewTitle}</Markdown></Typography>;
      }
    }
  }

  const renderProgress = () => {
    if (utils.isDefined(progress)) {
     return <Tooltip title={progressTooltip}
                     placement="bottom">
       <Box className="ViewBar-progress">
        <LinearProgress variant="determinate" value={progress}/>
        <Typography isLoading={isLoading} variant="body2">{progressText ?? `${progress}%`}</Typography>
      </Box>
     </Tooltip>
    }
  }

  const renderButtons = (actions) => {
    return actions.map((action, idx) => {
      if (action.label) {
        return <ActionButton key={idx}
                             action={action}/>
      } else {
        return <ActionIconButton key={idx}
                                 action={action}/>
      }
    });
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  if (view && (view?.id !== 'default')) {
    return <StyledViewBar ref={ref} {...innerProps}>
      {renderTitle()}
      {renderProgress()}
      <Box className="ViewBar-actions">
        {actions?.length > 0 ? renderButtons(actions) : null}
        {renderButtons([closeAction])}
      </Box>
      {innerProps.children}
    </StyledViewBar>
  }
});

ViewBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.string,
  progress: PropTypes.number,
  progressText: PropTypes.string,
  progressTooltip: PropTypes.any
};

export default ViewBar;
